import React, { useState } from "react";
import { UseComboboxStateChange } from "downshift";

import { ComposableHeaderDropdown, IndexedObject, Search } from "@themuse/design-system";
import { fetchCompanySuggestions, CompanySuggestion } from "@util/api";
import { isBrowser } from "@util/isBrowser";
import { pushDataLayerEvent } from "@util/tracking";

import homepageStyles from "./HomepageSearchDropdown.module.scss";
import adviceHeaderStyles from "./AdviceHeaderSearchDropdown.module.scss";

export const formatCompanySuggetsions = (companySuggestions: CompanySuggestion[]) =>
    companySuggestions.slice(0, 4).map((companyResult: CompanySuggestion) => {
        const { text, value } = companyResult;
        const { short_name } = value;
        return {
            label: `${text}`,
            value: `/profiles/${short_name}`
        };
});

export const updateInput = async (
    changes: UseComboboxStateChange<IndexedObject>,
    setItems: (items: IndexedObject[]) => void,
    setInputValue: (inputValue: string) => void,
    fetchData:(apiBase: string, inputValue: string) => Promise<CompanySuggestion[]>
) => {
    const { inputValue } = changes;
    const jobSearchItem = {
        label: `${inputValue}`,
        value: `/search/keyword/${inputValue}/`,
    };
    setInputValue(inputValue);
    if (inputValue) {
        setItems([jobSearchItem]);
        const companyResults = await fetchData("", inputValue);
        const companyList = companyResults?.length ? formatCompanySuggetsions(companyResults) : [];
        setItems([jobSearchItem].concat(companyList));
    } else {
        setItems([]);
    }
};

export const SearchDropdown = (props: { pageLocation: string }) => {
    const { pageLocation } = props;
    const [dropdownItems, setDropdownItems] = useState([] as IndexedObject[]);
    const [inputValue, setInputValue] = useState("");

    const NoResultsMessage = () => (
        <a href="/search" rel="noreferrer" target="_blank">Search All Jobs</a>
    );

    const searchQuerySubmittedEvent = (selectedItemText: string) => {
        const searchTerm = selectedItemText ? selectedItemText : inputValue;
        const eventProps = {
            search_term: searchTerm || ""
        };
        pushDataLayerEvent("searchQuerySubmitted", eventProps);
    };

    const onKeyboardSelectItem = (event: React.KeyboardEvent<HTMLInputElement>, selectedItem: IndexedObject) => {
        if (!isBrowser()) return "";

        const searchPath = inputValue
            ? `/search/keyword/${inputValue}`
            : "/search";
        const path = selectedItem?.value
            ? selectedItem?.value
            : searchPath;

        searchQuerySubmittedEvent(selectedItem?.label);

        return window.open(path, "_blank").focus();
    };

    const onMouseSelectItem = (event: React.MouseEvent<HTMLInputElement>, selectedItem: IndexedObject) => {
        searchQuerySubmittedEvent(selectedItem.label);
    };

    const homepageClasses = {
        container: homepageStyles.container,
        containerOpened: homepageStyles.containerOpened,
        inputContainer: homepageStyles.inputContainer,
        inputContainerOpened: homepageStyles.inputContainerOpened,
        input: homepageStyles.input,
        menuOpened: homepageStyles.menuOpened,
        menuList: homepageStyles.menuList,
        listItem: homepageStyles.listItem,
        listItemText: homepageStyles.listItemText,
        inputClearButton: homepageStyles.inputClearButton,
        postLabel: homepageStyles.postLabel,
    };

    const adviceClasses = {
        container: adviceHeaderStyles.container,
        containerOpened: adviceHeaderStyles.containerOpened,
        inputContainer: adviceHeaderStyles.inputContainer,
        inputContainerOpened: adviceHeaderStyles.inputContainerOpened,
        input: adviceHeaderStyles.input,
        menuOpened: adviceHeaderStyles.menuOpened,
        menuList: adviceHeaderStyles.menuList,
        listItem: adviceHeaderStyles.listItem,
        listItemText: adviceHeaderStyles.listItemText,
        closeButton: adviceHeaderStyles.closeButton,
        inputClearButton: adviceHeaderStyles.inputClearButton,
        clearButton: adviceHeaderStyles.clearButton,
        postLabel: adviceHeaderStyles.postLabel,
    };

    const dropdownProps = {
        classes: pageLocation === "homepage" ? homepageClasses : adviceClasses,
        dropdownProps: {
            IconComponent: <Search />,
            debounceWait: 10,
            dropdownList: dropdownItems,
            inputId: "search-input",
            onInputChange: (changes: UseComboboxStateChange<IndexedObject>) => updateInput(
                changes,
                setDropdownItems,
                setInputValue,
                fetchCompanySuggestions
            ),
            noResultsMessage: " ",
            onKeyboardSelectItem,
            onMouseSelectItem
        }
    };

    return <ComposableHeaderDropdown {...dropdownProps} />;
};
